// MACHINERY V2 (Stroje) — V4 shop-floor wall: the visible way out.
// Owner ruling 2026-08-24, after testing the shipped wall on .cc.
//
// ============================================================
//  WHY A VISIBLE CONTROL EXISTS ON A SURFACE THAT REFUSED ONE
// ============================================================
// M2.14 shipped the wall with an INVISIBLE exit only: Esc, taught by a chip
// that faded in on pointer movement. The owner ruled that the wall must also
// carry a persistent X in the top-right corner, "just like we have in the
// Settings menu" — always visible, never pointer-revealed.
//
// That supersedes the kiosk-purity half of the earlier door ruling. The rule is
// now "NO INTERACTIONS ON THE DATA SURFACE": every machine tile is still
// completely inert (WallTile.tsx's header), and THIS control — header chrome,
// about the page rather than about any machine — is sanctioned by the ruling.
// It is the only focusable node the wall renders.
//
// The pointer-revealed chip it replaces was DELETED, not kept beside it. The
// chip's entire purpose was discoverability; a persistent glyph serves that
// strictly better, and serves it to the first-time user who has not yet moved a
// mouse to summon anything. Two exit affordances on a surface designed to be
// glanced at from across a hall is one too many. Esc still works and is now an
// undocumented power-user shortcut (FleetWallView.tsx's keydown effect).
//
// ============================================================
//  THE SETTINGS IDIOM, AT A 44px HIT TARGET
// ============================================================
// The idiom being matched is SettingsV2DialogShell.tsx:226-235 (personally
// re-read at commit time): a `<button type="button" className="btn btn-ghost">`
// holding the `×` CHARACTER — not an SVG — at `{ width: 30, padding: 0,
// fontSize: 15, color: "var(--muted)" }`.
//
// `.btn` and `.btn-ghost` are ALREADY IN SCOPE here: they are defined under
// `.machinery-v2-root` at machinery-v2-tokens.scss:165-186 and :196-201, and
// MachineryV2Layout.tsx:74 puts that class on the element wrapping the
// <Outlet/> this wall renders through (all three personally re-read at commit
// time). Reusing them is also the only way to get hover at all — a `:hover`
// rule cannot live in an inline style, which is precisely why the token sheet
// carries these atoms (its own note at :162-164).
//
// >>> THE 30px VISUAL AND THE 44px HIT TARGET ARE THE SAME BUTTON. <<<
// The manager requires a >=44x44 target (the desktop-density exemption covered
// artboard-final controls; this one is ours, not the artboard's). The Settings
// idiom paints 30x30. Simply growing the button to 44 would satisfy the target
// but paint a 44x44 ghost pill on hover — visibly heavier than the control the
// owner pointed at. So the PAINTED box stays 30x30 and an absolutely
// positioned, fully transparent child stretches HIT_INSET past every edge.
//
// >>> THE INSET IS MEASURED FROM 28, NOT FROM 30. <<<
// The naive reading is 30 + 7 + 7 = 44. It is wrong by 2px — the -7 this pass
// replaces produced a 42px target. Three token-sheet rules, each opened and
// re-read at commit time, set the real number:
//   :50   `.machinery-v2-root * { box-sizing: border-box; }` — so the inline
//         `width: 30` / `height: 30` below is a BORDER-box measurement.
//   :169  `.btn { border: 1px solid var(--border); }` — 1px on all four edges.
//   :198  `.btn-ghost { border-color: transparent; }` — that RECOLOURS the
//         border. It does not remove it. The width stays 1px.
// An absolutely positioned child resolves its insets against the nearest
// positioned ancestor's PADDING box, and a 30px border box minus 1px of border
// per side, at `padding: 0`, is a 28x28 padding box. So `inset: -8px` spans
// 28 + 8 + 8 = 44 in both axes, and -7 spans 42.
//
// `boxSizing: "content-box"` would make the naive 30 + 7 + 7 true, but it would
// also PAINT a 32x32 box and visibly diverge from the Settings X the owner
// pointed at. The painted box staying 30 is the whole requirement, so
// content-box is deliberately NOT used.
//
// NOT BROWSER-MEASURED: this environment runs no dev server (production build
// only), so 44 is the static derivation above, not an observed DevTools box.
//
// The span is a child of the <button>, so a pointer event anywhere in that
// region is an event inside the button and runs its onClick; being absolutely
// positioned it is not a flex item, so `.btn`'s `gap` and `align-items` never
// see it and it shifts no pixel. It is aria-hidden, carries no handler and is
// not focusable — it enlarges what is CLICKABLE and nothing else. Visual
// weight, resting and hovered, is unchanged from Settings.
//
// KEYBOARD. The ring comes from `.btn:focus-visible`, added to the token sheet
// at :183-186 by this same change: `.btn-ghost` is fully transparent at rest,
// so before it a Tab onto the wall's ONLY focusable node showed nothing but the
// UA default. It mirrors the module's existing ChromeNavV4 ring (:270-271)
// rather than inventing a second one.
//
// PLACEMENT IS NOT HERE. WallHeader pins this button absolutely to the header's
// top-right corner — see the comment beside its render site for why a plain
// last flex child of a WRAPPING header leaves that corner exactly in the
// 1280-1440 range this control exists for.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";

/** How far the transparent hit-area child is pushed past each edge of the
 *  button's 28x28 PADDING box — the containing block an absolutely positioned
 *  child resolves against — to reach the 44px target the manager requires:
 *  28 + 8 + 8 = 44. It is NOT half of (44 - 30); see this file's header for why
 *  a 30px border box measures 28 inside. */
const HIT_INSET = -8;

export interface WallExitButtonProps {
  /** THE wall's one exit — FleetPage's `exitWall`, the very same callback Esc
   *  runs (FleetWallView's keydown effect). Two triggers, one implementation;
   *  there is no second exit path here that could drift from it. */
  onExit: () => void;
}

const WallExitButton: React.FC<WallExitButtonProps> = ({ onExit }) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <button
      type="button"
      className="btn btn-ghost"
      // A real name, not the bare glyph: a screen reader announcing "times,
      // button" on a kiosk tells nobody anything. The wording is the module's
      // own — the pointer-revealed chip this control replaces carried the same
      // phrase behind a keyboard prefix ("Esc — back to overview" / "Esc —
      // späť na prehľad"). That key was retired with the chip; the phrase moved
      // onto the control it names, minus a prefix that no longer describes the
      // affordance (en "Back to overview" / sk "Späť na prehľad" — both re-read
      // in public/locales/{en,sk}/myMachinery.json at commit time).
      aria-label={translate("myMachinery:v2.fleet.wall.exitLabel")}
      onClick={onExit}
      style={{
        // Settings' four values (SettingsV2DialogShell.tsx:226-235), unchanged.
        // Then three this placement adds:
        //   height: 30 — REDUNDANT, not protective. `.btn` already declares
        //     `height: 30px` (tokens :166) and nothing stretches it: the header
        //     is `alignItems: "center"`, never `stretch`, and this button is no
        //     longer even a flex item of it (WallHeader pins it absolutely). It
        //     is kept only so both axes of the painted box are stated beside
        //     the HIT_INSET arithmetic, which needs both to be 30.
        //   justifyContent — `.btn` is `display: inline-flex` (tokens :174) at
        //     the default `flex-start`, so with `padding: 0` in an explicit
        //     30px box the glyph would otherwise sit flush against the left
        //     edge.
        //   position: relative — the anchor for the hit-area child below.
        width: 30,
        height: 30,
        padding: 0,
        fontSize: 15,
        color: "var(--muted)",
        justifyContent: "center",
        position: "relative",
      }}
    >
      ×
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: HIT_INSET,
          right: HIT_INSET,
          bottom: HIT_INSET,
          left: HIT_INSET,
        }}
      />
    </button>
  );
};

export default WallExitButton;
